import { createApiRef, createRouteRef, createPlugin, createApiFactory, discoveryApiRef, identityApiRef, configApiRef, createRoutableExtension, createComponentExtension, useApi, alertApiRef, attachComponentData } from '@backstage/core-plugin-api';
import moment from 'moment';
import React, { useState, useEffect, Children, isValidElement, Fragment } from 'react';
import 'react-router';
import { useAsync } from 'react-use';
import Alert from '@material-ui/lab/Alert';
import { IconButton, Menu, MenuItem, ListItemIcon, Typography, makeStyles, Chip, withStyles, Tooltip, createStyles, List, ListItem, ListItemText, TextField, InputAdornment, Card, CardHeader, CardContent, Accordion, AccordionSummary, AccordionDetails, Grid, ListItemSecondaryAction } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DoneAll from '@material-ui/icons/DoneAll';
import Done from '@material-ui/icons/Done';
import Warning from '@material-ui/icons/Warning';
import { Table, Progress, ItemCardGrid, StatusOK, StatusAborted, InfoCard, Page, Header, RoutedTabs, StatusError } from '@backstage/core-components';
import PersonIcon from '@material-ui/icons/Person';
import { Pagination, Alert as Alert$1 } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Bar, Line, Tooltip as Tooltip$1, Legend, ScatterChart, Scatter } from 'recharts';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import domtoimage from 'dom-to-image';
import fileDownload from 'js-file-download';
import stc from 'string-to-color';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const opsgenieApiRef = createApiRef({
  id: "plugin.opsgenie.service"
});
const DEFAULT_PROXY_PATH = "/opsgenie/api";
class OpsgenieApi {
  constructor(opts) {
    var _a;
    this.discoveryApi = opts.discoveryApi;
    this.identityApi = opts.identityApi;
    this.domain = opts.domain;
    this.proxyPath = (_a = opts.proxyPath) != null ? _a : DEFAULT_PROXY_PATH;
  }
  async fetch(input, init) {
    const apiUrl = await this.apiUrl();
    const authedInit = await this.addAuthHeaders(init || {});
    const resp = await fetch(`${apiUrl}${input}`, authedInit);
    if (!resp.ok) {
      throw new Error(`Request failed with ${resp.status} ${resp.statusText}`);
    }
    return await resp.json();
  }
  async call(input, init) {
    const apiUrl = await this.apiUrl();
    const authedInit = await this.addAuthHeaders(init || {});
    const resp = await fetch(`${apiUrl}${input}`, authedInit);
    if (!resp.ok)
      throw new Error(`Request failed with ${resp.status}: ${resp.statusText}`);
  }
  async getAlerts(opts) {
    const limit = (opts == null ? void 0 : opts.limit) || 50;
    const query = (opts == null ? void 0 : opts.query) ? `&query=${opts == null ? void 0 : opts.query}` : "";
    const response = await this.fetch(`/v2/alerts?limit=${limit}${query}`);
    return response.data;
  }
  async getIncidents(opts) {
    const limit = (opts == null ? void 0 : opts.limit) || 50;
    const sort = (opts == null ? void 0 : opts.sort) || "createdAt";
    const order = (opts == null ? void 0 : opts.order) || "desc";
    const query = (opts == null ? void 0 : opts.query) ? `&query=${opts == null ? void 0 : opts.query}` : "";
    let response = await this.fetch(`/v1/incidents?limit=${limit}&sort=${sort}&order=${order}${query}`);
    let incidents = response.data;
    while (response.paging.next) {
      const parsedUrl = new URL(response.paging.next);
      response = await this.fetch(parsedUrl.pathname + parsedUrl.search);
      incidents = incidents.concat(response.data);
    }
    return incidents;
  }
  async acknowledgeAlert(alert) {
    const init = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: "Backstage \u2014 Opsgenie plugin" })
    };
    await this.call(`/v2/alerts/${alert.id}/acknowledge`, init);
  }
  async closeAlert(alert) {
    const init = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: "Backstage \u2014 Opsgenie plugin" })
    };
    await this.call(`/v2/alerts/${alert.id}/close`, init);
  }
  async getSchedules() {
    const response = await this.fetch("/v2/schedules");
    return response.data;
  }
  async getSchedulesForTeam(name) {
    const response = await this.fetch("/v2/schedules");
    return response.data.filter((schedule) => schedule.ownerTeam.name === name);
  }
  async getTeams() {
    const response = await this.fetch("/v2/teams");
    return response.data;
  }
  async getOnCall(scheduleId) {
    const response = await this.fetch(`/v2/schedules/${scheduleId}/on-calls`);
    return response.data.onCallParticipants;
  }
  getAlertDetailsURL(alert) {
    return `${this.domain}/alert/detail/${alert.id}/details`;
  }
  getIncidentDetailsURL(incident) {
    return `${this.domain}/incident/detail/${incident.id}`;
  }
  getUserDetailsURL(userId) {
    return `${this.domain}/settings/users/${userId}/detail`;
  }
  async apiUrl() {
    const proxyUrl = await this.discoveryApi.getBaseUrl("proxy");
    return proxyUrl + this.proxyPath;
  }
  async addAuthHeaders(init) {
    const { token } = await this.identityApi.getCredentials();
    const headers = init.headers || {};
    return {
      ...init,
      headers: {
        ...headers,
        ...token ? { Authorization: `Bearer ${token}` } : {}
      }
    };
  }
}

const UNKNOWN_TEAM_NAME = "Unknown";
const DEFAULT_BUSINESS_HOURS_START = 9;
const DEFAULT_BUSINESS_HOURS_END = 18;
const analyticsApiRef = createApiRef({
  id: "plugin.opsgenie.analytics"
});
const teamName = (teams, teamId) => {
  for (const team of teams) {
    if (team.id === teamId) {
      return team.name;
    }
  }
  return UNKNOWN_TEAM_NAME;
};
const respondingTeam = (teams, incident) => {
  if (incident.extraProperties.responders) {
    return incident.extraProperties.responders;
  }
  const teamResponders = incident.responders.filter((responderRef) => responderRef.type === "team");
  if (teamResponders.length === 0) {
    return UNKNOWN_TEAM_NAME;
  }
  return teamName(teams, teamResponders[0].id);
};
const sortByDate = (data) => {
  data.sort((a, b) => {
    if (a.date < b.date) {
      return -1;
    }
    if (a.date > b.date) {
      return 1;
    }
    return 0;
  });
};
class AnalitycsApi {
  constructor(opts) {
    this.businessHours = opts.businessHours;
  }
  incidentsByHour(context) {
    const incidentsBuckets = {};
    for (let h = 0; h <= 23; h++) {
      incidentsBuckets[h] = 0;
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      incidentsBuckets[incidentDate.hour()] += 1;
    });
    const data = Object.keys(incidentsBuckets).map((hour) => ({
      hour,
      total: incidentsBuckets[hour]
    }));
    data.sort((a, b) => parseInt(a.hour, 10) - parseInt(b.hour, 10));
    return data;
  }
  incidentsByDay(context) {
    const incidentsBuckets = {};
    for (let d = 0; d < 7; d++) {
      incidentsBuckets[d] = 0;
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      incidentsBuckets[incidentDate.day()] += 1;
    });
    const data = Object.keys(incidentsBuckets).map((day) => ({
      day: moment().day(day).format("dddd"),
      dayNum: parseInt(day, 10),
      total: incidentsBuckets[day]
    }));
    data.sort((a, b) => (a.dayNum + 6) % 7 - (b.dayNum + 6) % 7);
    return data;
  }
  incidentsByWeekAndSeverity(context) {
    const incidentsBuckets = {};
    const minDate = context.from.clone().startOf("isoWeek");
    const maxDate = context.to.clone().startOf("isoWeek");
    while (minDate <= maxDate) {
      const week = `w${minDate.isoWeek()} - ${minDate.year()}`;
      if (!incidentsBuckets[week]) {
        incidentsBuckets[week] = {
          p1: 0,
          p2: 0,
          p3: 0,
          p4: 0,
          p5: 0,
          date: minDate.clone()
        };
      }
      minDate.add(1, "weeks");
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      const week = `w${incidentDate.isoWeek()} - ${incidentDate.year()}`;
      if (incident.priority === "P1") {
        incidentsBuckets[week].p1 += 1;
      } else if (incident.priority === "P2") {
        incidentsBuckets[week].p2 += 1;
      } else if (incident.priority === "P3") {
        incidentsBuckets[week].p3 += 1;
      } else if (incident.priority === "P4") {
        incidentsBuckets[week].p4 += 1;
      } else if (incident.priority === "P5") {
        incidentsBuckets[week].p5 += 1;
      }
    });
    const data = Object.keys(incidentsBuckets).map((week) => ({
      week,
      p1: incidentsBuckets[week].p1,
      p2: incidentsBuckets[week].p2,
      p3: incidentsBuckets[week].p3,
      p4: incidentsBuckets[week].p4,
      p5: incidentsBuckets[week].p5,
      date: incidentsBuckets[week].date
    }));
    sortByDate(data);
    return data;
  }
  incidentsByWeekAndHours(context) {
    const incidentsBuckets = {};
    const minDate = context.from.clone().startOf("isoWeek");
    const maxDate = context.to.clone().startOf("isoWeek");
    while (minDate <= maxDate) {
      const week = `w${minDate.isoWeek()} - ${minDate.year()}`;
      if (!incidentsBuckets[week]) {
        incidentsBuckets[week] = {
          businessHours: 0,
          onCallHours: 0,
          total: 0,
          date: minDate.clone()
        };
      }
      minDate.add(1, "weeks");
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      const week = `w${incidentDate.isoWeek()} - ${incidentDate.year()}`;
      incidentsBuckets[week].total += 1;
      if (this.isBusinessHours(incidentDate)) {
        incidentsBuckets[week].businessHours += 1;
      } else {
        incidentsBuckets[week].onCallHours += 1;
      }
    });
    const data = Object.keys(incidentsBuckets).map((week) => ({
      week,
      businessHours: incidentsBuckets[week].businessHours,
      onCallHours: incidentsBuckets[week].onCallHours,
      total: incidentsBuckets[week].total,
      date: incidentsBuckets[week].date
    }));
    sortByDate(data);
    return data;
  }
  incidentsByDayAndResponder(context) {
    const incidentsBuckets = {};
    const respondersMap = {};
    for (let d = 0; d < 7; d++) {
      incidentsBuckets[d] = {
        total: 0,
        responders: {}
      };
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      const day = incidentDate.day();
      const responder = respondingTeam(context.teams, incident);
      respondersMap[responder] = true;
      if (!incidentsBuckets[day].responders[responder]) {
        incidentsBuckets[day].responders[responder] = 0;
      }
      incidentsBuckets[day].responders[responder] += 1;
      incidentsBuckets[day].total += 1;
    });
    const data = Object.keys(incidentsBuckets).map((day) => {
      const dataPoint = {
        period: moment().day(day).format("dddd"),
        dayNum: parseInt(day, 10),
        total: incidentsBuckets[day].total
      };
      Object.keys(respondersMap).forEach((responder) => {
        dataPoint[responder] = incidentsBuckets[day].responders[responder] || 0;
      });
      return dataPoint;
    });
    data.sort((a, b) => (a.dayNum + 6) % 7 - (b.dayNum + 6) % 7);
    return {
      dataPoints: data,
      responders: Object.keys(respondersMap)
    };
  }
  incidentsByMonthAndResponder(context) {
    const incidentsBuckets = {};
    const respondersMap = {};
    const from = context.from.clone();
    const to = context.to.clone();
    while (from <= to) {
      const month = `${from.month() + 1}/${from.year()}`;
      if (!incidentsBuckets[month]) {
        incidentsBuckets[month] = {
          responders: {},
          total: 0,
          date: from.clone()
        };
      }
      from.add(1, "month");
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      const month = `${incidentDate.month() + 1}/${incidentDate.year()}`;
      const responder = respondingTeam(context.teams, incident);
      respondersMap[responder] = true;
      if (!incidentsBuckets[month].responders[responder]) {
        incidentsBuckets[month].responders[responder] = 0;
      }
      incidentsBuckets[month].responders[responder] += 1;
      incidentsBuckets[month].total += 1;
    });
    const data = Object.keys(incidentsBuckets).map((month) => {
      const dataPoint = {
        period: month,
        total: incidentsBuckets[month].total,
        date: incidentsBuckets[month].date
      };
      Object.keys(respondersMap).forEach((responder) => {
        dataPoint[responder] = incidentsBuckets[month].responders[responder] || 0;
      });
      return dataPoint;
    });
    sortByDate(data);
    return {
      dataPoints: data,
      responders: Object.keys(respondersMap)
    };
  }
  incidentsByWeekAndResponder(context) {
    const incidentsBuckets = {};
    const respondersMap = {};
    const minDate = context.from.clone().startOf("isoWeek");
    const maxDate = context.to.clone().startOf("isoWeek");
    while (minDate <= maxDate) {
      const week = `w${minDate.isoWeek()} - ${minDate.year()}`;
      if (!incidentsBuckets[week]) {
        incidentsBuckets[week] = {
          responders: {},
          total: 0,
          date: minDate.clone()
        };
      }
      minDate.add(1, "weeks");
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      const week = `w${incidentDate.isoWeek()} - ${incidentDate.year()}`;
      const responder = respondingTeam(context.teams, incident);
      respondersMap[responder] = true;
      if (!incidentsBuckets[week].responders[responder]) {
        incidentsBuckets[week].responders[responder] = 0;
      }
      incidentsBuckets[week].responders[responder] += 1;
      incidentsBuckets[week].total += 1;
    });
    const data = Object.keys(incidentsBuckets).map((week) => {
      const dataPoint = {
        period: week,
        total: incidentsBuckets[week].total,
        date: incidentsBuckets[week].date
      };
      Object.keys(respondersMap).forEach((responder) => {
        dataPoint[responder] = incidentsBuckets[week].responders[responder] || 0;
      });
      return dataPoint;
    });
    sortByDate(data);
    return {
      dataPoints: data,
      responders: Object.keys(respondersMap)
    };
  }
  incidentsByQuarterAndResponder(context) {
    const incidentsBuckets = {};
    const respondersMap = {};
    const from = context.from.clone();
    const to = context.to.clone();
    while (from <= to) {
      const quarter = `Q${from.quarter()} - ${from.year()}`;
      if (!incidentsBuckets[quarter]) {
        incidentsBuckets[quarter] = {
          responders: {},
          total: 0,
          date: from.clone()
        };
      }
      from.add(1, "quarter");
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      const quarter = `Q${incidentDate.quarter()} - ${incidentDate.year()}`;
      const responder = respondingTeam(context.teams, incident);
      respondersMap[responder] = true;
      if (!incidentsBuckets[quarter].responders[responder]) {
        incidentsBuckets[quarter].responders[responder] = 0;
      }
      incidentsBuckets[quarter].responders[responder] += 1;
      incidentsBuckets[quarter].total += 1;
    });
    const data = Object.keys(incidentsBuckets).map((quarter) => {
      const dataPoint = {
        period: quarter,
        total: incidentsBuckets[quarter].total,
        date: incidentsBuckets[quarter].date
      };
      Object.keys(respondersMap).forEach((responder) => {
        dataPoint[responder] = incidentsBuckets[quarter].responders[responder] || 0;
      });
      return dataPoint;
    });
    sortByDate(data);
    return {
      dataPoints: data,
      responders: Object.keys(respondersMap)
    };
  }
  impactByWeekAndResponder(context) {
    const incidentsBuckets = {};
    const respondersMap = {};
    const minDate = context.from.clone().startOf("isoWeek");
    const maxDate = context.to.clone().startOf("isoWeek");
    const average = (durations) => durations.length === 0 ? 0 : durations.reduce((a, b) => a + b, 0) / durations.length;
    while (minDate <= maxDate) {
      const week = `w${minDate.isoWeek()} - ${minDate.year()}`;
      if (!incidentsBuckets[week]) {
        incidentsBuckets[week] = {
          responders: {},
          durations: [],
          date: minDate.clone()
        };
      }
      minDate.add(1, "weeks");
    }
    context.incidents.forEach((incident) => {
      const incidentDate = moment(incident.impactStartDate);
      const incidentEnd = moment(incident.impactEndDate);
      const week = `w${incidentDate.isoWeek()} - ${incidentDate.year()}`;
      const responder = respondingTeam(context.teams, incident);
      const impactDuration = incidentEnd.diff(incidentDate, "minutes");
      respondersMap[responder] = true;
      if (!incidentsBuckets[week].responders[responder]) {
        incidentsBuckets[week].responders[responder] = [];
      }
      incidentsBuckets[week].responders[responder].push(impactDuration);
      incidentsBuckets[week].durations.push(impactDuration);
    });
    const data = Object.keys(incidentsBuckets).map((week) => {
      const dataPoint = {
        period: week,
        total: average(incidentsBuckets[week].durations),
        date: incidentsBuckets[week].date
      };
      Object.keys(respondersMap).forEach((responder) => {
        dataPoint[responder] = incidentsBuckets[week].responders[responder] ? average(incidentsBuckets[week].responders[responder]) : 0;
      });
      return dataPoint;
    });
    sortByDate(data);
    return {
      dataPoints: data,
      responders: Object.keys(respondersMap)
    };
  }
  isBusinessHours(incidentStartedAt) {
    return incidentStartedAt.hour() >= this.businessHours.start && incidentStartedAt.hour() < this.businessHours.end;
  }
}

const opsgenieRouteRef = createRouteRef({
  id: "opsgenie"
});
const opsGeniePlugin = createPlugin({
  id: "opsgenie",
  apis: [
    createApiFactory({
      api: opsgenieApiRef,
      deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef, configApi: configApiRef },
      factory: ({ discoveryApi, identityApi, configApi }) => {
        return new OpsgenieApi({
          discoveryApi,
          identityApi,
          domain: configApi.getString("opsgenie.domain"),
          proxyPath: configApi.getOptionalString("opsgenie.proxyPath")
        });
      }
    }),
    createApiFactory({
      api: analyticsApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => {
        return new AnalitycsApi({
          businessHours: {
            start: configApi.getOptionalNumber("opsgenie.analytics.businessHours.start") || DEFAULT_BUSINESS_HOURS_START,
            end: configApi.getOptionalNumber("opsgenie.analytics.businessHours.end") || DEFAULT_BUSINESS_HOURS_END
          }
        });
      }
    })
  ],
  routes: {
    explore: opsgenieRouteRef
  }
});

const OpsgeniePage = opsGeniePlugin.provide(createRoutableExtension({
  name: "OpsgeniePage",
  component: () => import('./index-32dae66a.esm.js').then((m) => m.OpsgeniePage),
  mountPoint: opsgenieRouteRef
}));
const EntityOpsgenieAlertsCard = opsGeniePlugin.provide(createComponentExtension({
  name: "EntityOpsgenieAlertsCard",
  component: {
    lazy: () => import('./index-4cf6d8aa.esm.js').then((m) => m.AlertsCard)
  }
}));
const EntityOpsgenieOnCallListCard = opsGeniePlugin.provide(createComponentExtension({
  name: "EntityOpsgenieOnCallListCard",
  component: {
    lazy: () => import('./index-4cf6d8aa.esm.js').then((m) => m.OnCallListCard)
  }
}));

const OPSGENIE_ANNOTATION = "opsgenie.com/component-selector";
const OPSGENIE_TEAM_ANNOTATION = "opsgenie.com/team";
const isOpsgenieAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity.metadata.annotations) == null ? void 0 : _a[OPSGENIE_ANNOTATION]);
};
const isOpsgenieOnCallListAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity.metadata.annotations) == null ? void 0 : _a[OPSGENIE_TEAM_ANNOTATION]);
};

const AlertActionsMenu = ({ alert, onAlertChanged }) => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const alertApi = useApi(alertApiRef);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const callback = onAlertChanged || ((_) => {
  });
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const handleAcknowledge = async () => {
    try {
      await opsgenieApi.acknowledgeAlert(alert);
      handleCloseMenu();
      alertApi.post({ message: "Alert acknowledged." });
      alert.acknowledged = true;
      callback(alert);
    } catch (err) {
      alertApi.post({ message: `Could not acknowledge alert: ${err}`, severity: "error" });
    }
  };
  const handleCloseAlert = async () => {
    try {
      await opsgenieApi.closeAlert(alert);
      handleCloseMenu();
      alertApi.post({ message: "Alert closed." });
      alert.status = "closed";
      callback(alert);
    } catch (err) {
      alertApi.post({ message: `Could not close alert: ${err}`, severity: "error" });
    }
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(IconButton, {
    "aria-label": "more",
    "aria-controls": "long-menu",
    "aria-haspopup": "true",
    onClick: handleClick
  }, /* @__PURE__ */ React.createElement(MoreVertIcon, null)), /* @__PURE__ */ React.createElement(Menu, {
    id: `actions-menu-${alert.id}`,
    anchorEl,
    keepMounted: true,
    open: Boolean(anchorEl),
    onClose: handleCloseMenu,
    PaperProps: {
      style: { maxHeight: 48 * 4.5 }
    }
  }, !alert.acknowledged && alert.status !== "closed" && /* @__PURE__ */ React.createElement(MenuItem, {
    key: "ack",
    onClick: handleAcknowledge
  }, /* @__PURE__ */ React.createElement(ListItemIcon, null, /* @__PURE__ */ React.createElement(DoneAll, {
    fontSize: "small"
  })), /* @__PURE__ */ React.createElement(Typography, {
    variant: "inherit",
    noWrap: true
  }, "Acknowledge")), alert.status !== "closed" && /* @__PURE__ */ React.createElement(MenuItem, {
    key: "close",
    onClick: handleCloseAlert
  }, /* @__PURE__ */ React.createElement(ListItemIcon, null, /* @__PURE__ */ React.createElement(CheckCircleIcon, {
    fontSize: "small"
  })), /* @__PURE__ */ React.createElement(Typography, {
    variant: "inherit",
    noWrap: true
  }, "Close")), /* @__PURE__ */ React.createElement(MenuItem, {
    key: "details",
    onClick: handleCloseMenu
  }, /* @__PURE__ */ React.createElement(ListItemIcon, null, /* @__PURE__ */ React.createElement(OpenInNewIcon, {
    fontSize: "small"
  })), /* @__PURE__ */ React.createElement(Typography, {
    variant: "inherit",
    noWrap: true
  }, /* @__PURE__ */ React.createElement(Link, {
    href: opsgenieApi.getAlertDetailsURL(alert)
  }, "View in Opsgenie")))));
};

const useStyles$5 = makeStyles((theme) => ({
  warning: {
    borderColor: theme.palette.status.warning,
    color: theme.palette.status.warning,
    "& *": {
      color: theme.palette.status.warning
    }
  },
  error: {
    borderColor: theme.palette.status.error,
    color: theme.palette.status.error,
    "& *": {
      color: theme.palette.status.error
    }
  },
  closed: {
    borderColor: theme.palette.status.ok,
    color: theme.palette.status.ok,
    "& *": {
      color: theme.palette.status.ok
    }
  }
}));
const StatusChip = ({ alert }) => {
  const classes = useStyles$5();
  let chipClass = classes.error;
  let label = "Unknown";
  let icon = /* @__PURE__ */ React.createElement(Warning, null);
  if (alert.status === "open" && !alert.acknowledged && !alert.isSeen) {
    chipClass = classes.error;
    label = "Open";
    icon = /* @__PURE__ */ React.createElement(Warning, null);
  } else if (alert.status === "open" && !alert.acknowledged && alert.isSeen) {
    chipClass = classes.error;
    label = "Seen";
    icon = /* @__PURE__ */ React.createElement(Done, null);
  } else if (alert.status === "open" && alert.acknowledged) {
    chipClass = classes.warning;
    label = "Acknowledged";
    icon = /* @__PURE__ */ React.createElement(DoneAll, null);
  } else if (alert.status === "closed") {
    chipClass = classes.closed;
    label = "Closed";
  }
  return /* @__PURE__ */ React.createElement(Chip, {
    label,
    size: "small",
    variant: "outlined",
    icon: alert.status === "closed" ? void 0 : icon,
    className: chipClass
  });
};

const P5Chip = withStyles({
  root: {
    backgroundColor: "#00857A",
    color: "white"
  }
})(Chip);
const P4Chip = withStyles({
  root: {
    backgroundColor: "#36b37e",
    color: "white"
  }
})(Chip);
const P3Chip = withStyles({
  root: {
    backgroundColor: "#ffab00",
    color: "white"
  }
})(Chip);
const P2Chip = withStyles({
  root: {
    backgroundColor: "#ff7452",
    color: "white"
  }
})(Chip);
const P1Chip = withStyles({
  root: {
    backgroundColor: "#bf2600",
    color: "white"
  }
})(Chip);
const priorityLabels = {
  "P5": "Informational",
  "P4": "Low",
  "P3": "Moderate",
  "P2": "High",
  "P1": "Critical"
};
const PriorityChip = ({ priority }) => {
  let chip = /* @__PURE__ */ React.createElement(React.Fragment, null);
  switch (priority) {
    case "P5":
      chip = /* @__PURE__ */ React.createElement(P5Chip, {
        label: priority,
        size: "small"
      });
      break;
    case "P4":
      chip = /* @__PURE__ */ React.createElement(P4Chip, {
        label: priority,
        size: "small"
      });
      break;
    case "P3":
      chip = /* @__PURE__ */ React.createElement(P3Chip, {
        label: priority,
        size: "small"
      });
      break;
    case "P2":
      chip = /* @__PURE__ */ React.createElement(P2Chip, {
        label: priority,
        size: "small"
      });
      break;
    case "P1":
      chip = /* @__PURE__ */ React.createElement(P1Chip, {
        label: priority,
        size: "small"
      });
      break;
    default:
      chip = /* @__PURE__ */ React.createElement(Chip, {
        label: priority,
        size: "small"
      });
  }
  return /* @__PURE__ */ React.createElement(Tooltip, {
    title: priorityLabels[priority]
  }, /* @__PURE__ */ React.createElement("div", null, chip));
};

const AlertsTable = ({ alerts }) => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const smallColumnStyle = {
    width: "5%",
    maxWidth: "5%"
  };
  const mediumColumnStyle = {
    width: "10%",
    maxWidth: "10%"
  };
  const [alertsList, setAlertsList] = useState(alerts);
  const onAlertChanged = (newAlert) => {
    setAlertsList(alertsList.map((alert) => {
      if (newAlert.id === alert.id) {
        return newAlert;
      }
      return alert;
    }));
  };
  const columns = [
    {
      title: "ID",
      field: "tinyId",
      highlight: true,
      cellStyle: smallColumnStyle,
      headerStyle: smallColumnStyle,
      render: (rowData) => /* @__PURE__ */ React.createElement(Link, {
        href: opsgenieApi.getAlertDetailsURL(rowData)
      }, "#", rowData.tinyId)
    },
    {
      title: "Priority",
      field: "priority",
      cellStyle: smallColumnStyle,
      headerStyle: smallColumnStyle,
      render: (rowData) => /* @__PURE__ */ React.createElement(PriorityChip, {
        priority: rowData.priority
      })
    },
    {
      title: "Status",
      field: "status",
      cellStyle: smallColumnStyle,
      headerStyle: smallColumnStyle,
      render: (rowData) => /* @__PURE__ */ React.createElement(StatusChip, {
        alert: rowData
      })
    },
    {
      title: "Alert",
      field: "message"
    },
    {
      title: "Tags",
      field: "tags",
      render: (rowData) => /* @__PURE__ */ React.createElement(React.Fragment, null, rowData.tags.map((tag, i) => /* @__PURE__ */ React.createElement(Chip, {
        label: tag,
        key: i,
        size: "small"
      })))
    },
    {
      title: "Updated At",
      field: "updatedAt",
      type: "datetime",
      dateSetting: { locale: "en-UK" },
      cellStyle: mediumColumnStyle,
      headerStyle: mediumColumnStyle
    },
    {
      title: "Actions",
      field: "",
      cellStyle: smallColumnStyle,
      headerStyle: smallColumnStyle,
      render: (rowData) => /* @__PURE__ */ React.createElement(AlertActionsMenu, {
        alert: rowData,
        onAlertChanged
      })
    }
  ];
  return /* @__PURE__ */ React.createElement(Table, {
    options: {
      sorting: true,
      search: true,
      paging: true,
      actionsColumnIndex: -1,
      pageSize: 25,
      pageSizeOptions: [25, 50, 100, 150, 200],
      padding: "dense"
    },
    localization: { header: { actions: void 0 } },
    columns,
    data: alertsList
  });
};

const AlertsList = () => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const { value, loading, error } = useAsync(async () => await opsgenieApi.getAlerts());
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, {
      "data-testid": "error-message",
      severity: "error"
    }, error.message);
  }
  return /* @__PURE__ */ React.createElement(AlertsTable, {
    alerts: value
  });
};

const IncidentsTable = ({ incidents }) => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const smallColumnStyle = {
    width: "5%",
    maxWidth: "5%"
  };
  const mediumColumnStyle = {
    width: "10%",
    maxWidth: "10%"
  };
  const columns = [
    {
      title: "ID",
      field: "tinyId",
      highlight: true,
      cellStyle: smallColumnStyle,
      headerStyle: smallColumnStyle,
      render: (rowData) => /* @__PURE__ */ React.createElement(Link, {
        href: opsgenieApi.getIncidentDetailsURL(rowData)
      }, "#", rowData.tinyId)
    },
    {
      title: "Priority",
      field: "priority",
      cellStyle: smallColumnStyle,
      headerStyle: smallColumnStyle,
      render: (rowData) => /* @__PURE__ */ React.createElement(PriorityChip, {
        priority: rowData.priority
      })
    },
    {
      title: "Status",
      field: "status",
      cellStyle: smallColumnStyle,
      headerStyle: smallColumnStyle,
      render: (rowData) => /* @__PURE__ */ React.createElement(Chip, {
        label: rowData.status,
        color: rowData.status === "open" ? "secondary" : "default",
        size: "small"
      })
    },
    {
      title: "Description",
      field: "message"
    },
    {
      title: "Tags",
      field: "tags",
      render: (rowData) => /* @__PURE__ */ React.createElement(React.Fragment, null, rowData.tags.map((tag, i) => /* @__PURE__ */ React.createElement(Chip, {
        label: tag,
        key: i,
        size: "small"
      })))
    },
    {
      title: "Updated At",
      field: "updatedAt",
      type: "datetime",
      dateSetting: { locale: "en-UK" },
      cellStyle: mediumColumnStyle,
      headerStyle: mediumColumnStyle
    }
  ];
  return /* @__PURE__ */ React.createElement(Table, {
    options: {
      sorting: true,
      search: true,
      paging: true,
      actionsColumnIndex: -1,
      pageSize: 25,
      pageSizeOptions: [25, 50, 100, 150, 200],
      padding: "dense"
    },
    localization: { header: { actions: void 0 } },
    columns,
    data: incidents
  });
};

const IncidentsList = () => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const { value, loading, error } = useAsync(async () => await opsgenieApi.getIncidents());
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, {
      "data-testid": "error-message",
      severity: "error"
    }, error.message);
  }
  return /* @__PURE__ */ React.createElement(IncidentsTable, {
    incidents: value
  });
};

const useStyles$4 = makeStyles((theme) => createStyles({
  pagination: {
    marginTop: theme.spacing(2)
  },
  search: {
    marginBottom: theme.spacing(2)
  },
  onCallItemGrid: {
    gridTemplateColumns: "repeat(auto-fill, minmax(32em, 1fr))"
  }
}));
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};
const OnCallForScheduleList = ({ schedule }) => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const { value, loading, error } = useAsync(async () => await opsgenieApi.getOnCall(schedule.id));
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, {
      "data-testid": "error-message",
      severity: "error"
    }, error.message);
  }
  return /* @__PURE__ */ React.createElement(List, null, value.map((responder, i) => /* @__PURE__ */ React.createElement(ListItem, {
    key: i,
    button: true,
    component: "a",
    href: opsgenieApi.getUserDetailsURL(responder.id)
  }, /* @__PURE__ */ React.createElement(ListItemIcon, null, /* @__PURE__ */ React.createElement(PersonIcon, null)), /* @__PURE__ */ React.createElement(ListItemText, {
    primary: responder.name
  }))), value.length === 0 && /* @__PURE__ */ React.createElement(ListItem, null, /* @__PURE__ */ React.createElement(ListItemText, {
    primary: "\u26A0\uFE0F No one on-call."
  })));
};
const OnCallForScheduleCard = ({ schedule }) => {
  const title = /* @__PURE__ */ React.createElement("div", {
    style: { display: "flex" }
  }, /* @__PURE__ */ React.createElement(Tooltip, {
    title: schedule.enabled ? "Enabled" : "Disabled"
  }, /* @__PURE__ */ React.createElement("div", null, schedule.enabled ? /* @__PURE__ */ React.createElement(StatusOK, null) : /* @__PURE__ */ React.createElement(StatusAborted, null))), schedule.ownerTeam.name);
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(CardHeader, {
    title,
    titleTypographyProps: { variant: "h6" }
  }), /* @__PURE__ */ React.createElement(CardContent, null, /* @__PURE__ */ React.createElement(OnCallForScheduleList, {
    schedule
  })));
};
const SchedulesGrid = ({ schedules, cardsPerPage }) => {
  const classes = useStyles$4();
  const [results, setResults] = React.useState(schedules);
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [offset, setOffset] = React.useState(0);
  const handleChange = (_, value) => {
    setOffset((value - 1) * cardsPerPage);
    setPage(value);
  };
  const debouncedSearch = useDebounce(search, 300);
  useEffect(() => {
    if (!debouncedSearch) {
      setResults(schedules);
      return;
    }
    const filtered = schedules.filter((schedule) => {
      return schedule.name.toLowerCase().includes(debouncedSearch.toLowerCase());
    });
    setResults(filtered);
  }, [debouncedSearch, schedules]);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(TextField, {
    fullWidth: true,
    variant: "outlined",
    className: classes.search,
    placeholder: "Team\u2026",
    InputProps: {
      startAdornment: /* @__PURE__ */ React.createElement(InputAdornment, {
        position: "start"
      }, /* @__PURE__ */ React.createElement(SearchIcon, null))
    },
    onChange: (e) => setSearch(e.target.value)
  }), /* @__PURE__ */ React.createElement(ItemCardGrid, {
    classes: { root: classes.onCallItemGrid }
  }, results.filter((_, i) => i >= offset && i < offset + cardsPerPage).map((schedule) => /* @__PURE__ */ React.createElement(OnCallForScheduleCard, {
    key: schedule.id,
    schedule
  }))), /* @__PURE__ */ React.createElement(Pagination, {
    className: classes.pagination,
    count: Math.ceil(results.length / cardsPerPage),
    page,
    onChange: handleChange,
    showFirstButton: true,
    showLastButton: true
  }));
};
const OnCallList = ({ cardsPerPage }) => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const { value, loading, error } = useAsync(async () => await opsgenieApi.getSchedules());
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, {
      "data-testid": "error-message",
      severity: "error"
    }, error.message);
  }
  return /* @__PURE__ */ React.createElement(SchedulesGrid, {
    schedules: value.filter((schedule) => schedule.enabled),
    cardsPerPage: cardsPerPage || 6
  });
};

const exportGraph = (domNodeId) => {
  const node = document.getElementById(domNodeId);
  if (!node) {
    return;
  }
  domtoimage.toBlob(node, { bgcolor: "white" }).then((blob) => {
    fileDownload(blob, `${domNodeId}.png`);
  });
};
const SaveAction = ({ targetRef }) => {
  return /* @__PURE__ */ React.createElement(IconButton, {
    "aria-label": "save",
    title: "Save as image",
    onClick: () => exportGraph(targetRef)
  }, /* @__PURE__ */ React.createElement(SaveAltIcon, null));
};

const useStyles$3 = makeStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.background.default,
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingBottom: "0.5rem",
    paddingTop: "0.5rem",
    borderRadius: "5px",
    border: "1px solid white",
    borderColor: theme.palette.grey[300]
  }
}));
const FilterZeroTooltip = (props) => {
  const classes = useStyles$3();
  if (!props.active || !props.payload || !props.payload.length) {
    return null;
  }
  const filteredPayload = props.payload.filter((item) => item.payload[item.dataKey] !== 0);
  if (!filteredPayload.length) {
    return null;
  }
  const items = filteredPayload.map((row) => {
    let value = row.payload[row.dataKey];
    let name = row.name;
    if (props.formatter) {
      const formatted = props.formatter(value, name);
      if (Array.isArray(formatted)) {
        name = formatted[1];
        value = formatted[0];
      } else {
        value = formatted;
      }
    }
    return /* @__PURE__ */ React.createElement("p", {
      key: row.dataKey,
      style: { color: row.fill }
    }, `${name}: ${value}`);
  });
  return /* @__PURE__ */ React.createElement("div", {
    className: classes.tooltip
  }, items);
};

const Graph$3 = ({ context }) => {
  const analyticsApi = useApi(analyticsApiRef);
  const dataPoints = analyticsApi.incidentsByWeekAndHours(context);
  return /* @__PURE__ */ React.createElement("div", {
    id: "weekly-incidents",
    style: { width: "100%", height: 300, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(ResponsiveContainer, null, /* @__PURE__ */ React.createElement(ComposedChart, {
    data: dataPoints
  }, /* @__PURE__ */ React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /* @__PURE__ */ React.createElement(XAxis, {
    dataKey: "week"
  }), /* @__PURE__ */ React.createElement(YAxis, null), /* @__PURE__ */ React.createElement(Bar, {
    dataKey: "businessHours",
    fill: "#82ca9d",
    name: "Business hours",
    stackId: "a",
    barSize: 30
  }), /* @__PURE__ */ React.createElement(Bar, {
    dataKey: "onCallHours",
    fill: "#8884d8",
    name: "On-call hours",
    stackId: "a",
    barSize: 30
  }), /* @__PURE__ */ React.createElement(Line, {
    type: "monotone",
    dataKey: "total",
    name: "Total",
    stroke: "#ff7300"
  }), /* @__PURE__ */ React.createElement(Tooltip$1, {
    content: /* @__PURE__ */ React.createElement(FilterZeroTooltip, null)
  }), /* @__PURE__ */ React.createElement(Legend, null))));
};
const WeeklyIncidents = ({ context }) => {
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by week",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: "weekly-incidents"
    })
  }, /* @__PURE__ */ React.createElement(Graph$3, {
    context
  }));
};

const Graph$2 = ({ context }) => {
  const analyticsApi = useApi(analyticsApiRef);
  const dataPoints = analyticsApi.incidentsByWeekAndSeverity(context);
  return /* @__PURE__ */ React.createElement("div", {
    id: "weekly-incidents-severity",
    style: { width: "100%", height: 300, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(ResponsiveContainer, null, /* @__PURE__ */ React.createElement(ComposedChart, {
    data: dataPoints
  }, /* @__PURE__ */ React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /* @__PURE__ */ React.createElement(XAxis, {
    dataKey: "week"
  }), /* @__PURE__ */ React.createElement(YAxis, null), /* @__PURE__ */ React.createElement(Bar, {
    dataKey: "p1",
    fill: "#bf2600",
    name: "P1 - Critical",
    stackId: "a",
    barSize: 30
  }), /* @__PURE__ */ React.createElement(Bar, {
    dataKey: "p2",
    fill: "#ff7452",
    name: "P2 - High",
    stackId: "a",
    barSize: 30
  }), /* @__PURE__ */ React.createElement(Bar, {
    dataKey: "p3",
    fill: "#ffab00",
    name: "P3 - Moderate",
    stackId: "a",
    barSize: 30
  }), /* @__PURE__ */ React.createElement(Bar, {
    dataKey: "p4",
    fill: "#36b37e",
    name: "P4 - Low",
    stackId: "a",
    barSize: 30
  }), /* @__PURE__ */ React.createElement(Bar, {
    dataKey: "p5",
    fill: "#00857A",
    name: "P5 - Informational",
    stackId: "a",
    barSize: 30
  }), /* @__PURE__ */ React.createElement(Tooltip$1, {
    content: /* @__PURE__ */ React.createElement(FilterZeroTooltip, null)
  }), /* @__PURE__ */ React.createElement(Legend, null))));
};
const WeeklyIncidentsSeverity = ({ context }) => {
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by week and severity",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: "weekly-incidents-severity"
    })
  }, /* @__PURE__ */ React.createElement(Graph$2, {
    context
  }));
};

const colorForString = (() => {
  const preferredColors = [
    "#FF9830",
    "#ff7452",
    "#F2495C",
    "#F2CC0C",
    "#00857A",
    "#5794F2",
    "#144790",
    "#B877D9",
    "#255637",
    "#481D24",
    "#E56399"
  ];
  const namesMap = {};
  return (name) => {
    if (namesMap[name]) {
      return namesMap[name];
    }
    if (preferredColors.length === 0) {
      return stc(name);
    }
    namesMap[name] = preferredColors.pop();
    return namesMap[name];
  };
})();

const PeriodByResponderGraph = ({ data }) => {
  return /* @__PURE__ */ React.createElement(ResponsiveContainer, null, /* @__PURE__ */ React.createElement(ComposedChart, {
    data: data.dataPoints
  }, /* @__PURE__ */ React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /* @__PURE__ */ React.createElement(XAxis, {
    dataKey: "period"
  }), /* @__PURE__ */ React.createElement(YAxis, null), data.responders.map((responder) => /* @__PURE__ */ React.createElement(Bar, {
    dataKey: responder,
    fill: colorForString(responder),
    stackId: "a",
    barSize: 30,
    key: responder
  })), /* @__PURE__ */ React.createElement(Line, {
    type: "monotone",
    dataKey: "total",
    name: "Total",
    stroke: "#ff7300"
  }), /* @__PURE__ */ React.createElement(Tooltip$1, {
    content: /* @__PURE__ */ React.createElement(FilterZeroTooltip, null)
  }), /* @__PURE__ */ React.createElement(Legend, null)));
};

const WeeklyIncidentsResponders = ({ context }) => {
  const graphId = "weekly-incidents-responders";
  const analyticsApi = useApi(analyticsApiRef);
  const data = analyticsApi.incidentsByWeekAndResponder(context);
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by week and responder",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: graphId
    })
  }, /* @__PURE__ */ React.createElement("div", {
    id: graphId,
    style: { width: "100%", height: 450, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(PeriodByResponderGraph, {
    data
  })));
};

const QuarterlyIncidentsResponders = ({ context }) => {
  const graphId = "quarterly-incidents-responders";
  const analyticsApi = useApi(analyticsApiRef);
  const data = analyticsApi.incidentsByQuarterAndResponder(context);
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by quarter and responder",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: graphId
    })
  }, /* @__PURE__ */ React.createElement("div", {
    id: graphId,
    style: { width: "100%", height: 450, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(PeriodByResponderGraph, {
    data
  })));
};

const Graph$1 = ({ context }) => {
  const analyticsApi = useApi(analyticsApiRef);
  const dataPoints = analyticsApi.incidentsByHour(context);
  return /* @__PURE__ */ React.createElement("div", {
    id: "hourly-incidents",
    style: { width: "100%", height: 300, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(ResponsiveContainer, null, /* @__PURE__ */ React.createElement(ScatterChart, {
    data: dataPoints
  }, /* @__PURE__ */ React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /* @__PURE__ */ React.createElement(XAxis, {
    dataKey: "hour",
    name: "Hour",
    unit: "h"
  }), /* @__PURE__ */ React.createElement(YAxis, {
    dataKey: "total",
    name: "Total"
  }), /* @__PURE__ */ React.createElement(Tooltip$1, {
    content: /* @__PURE__ */ React.createElement(FilterZeroTooltip, null)
  }), /* @__PURE__ */ React.createElement(Scatter, {
    name: "Hour",
    data: dataPoints,
    fill: "#8884d8"
  }))));
};
const HourlyIncidents = ({ context }) => {
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by hour",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: "hourly-incidents"
    })
  }, /* @__PURE__ */ React.createElement(Graph$1, {
    context
  }));
};

const MonthlyIncidentsResponders = ({ context }) => {
  const graphId = "monthly-incidents-responders";
  const analyticsApi = useApi(analyticsApiRef);
  const data = analyticsApi.incidentsByMonthAndResponder(context);
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by month and responder",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: graphId
    })
  }, /* @__PURE__ */ React.createElement("div", {
    id: graphId,
    style: { width: "100%", height: 450, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(PeriodByResponderGraph, {
    data
  })));
};

const DailyIncidentsResponders = ({ context }) => {
  const graphId = "daily-incidents-responders";
  const analyticsApi = useApi(analyticsApiRef);
  const data = analyticsApi.incidentsByDayAndResponder(context);
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by day and responder",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: graphId
    })
  }, /* @__PURE__ */ React.createElement("div", {
    id: graphId,
    style: { width: "100%", height: 450, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(PeriodByResponderGraph, {
    data
  })));
};

const Graph = ({ context }) => {
  const analyticsApi = useApi(analyticsApiRef);
  const dataPoints = analyticsApi.incidentsByDay(context);
  return /* @__PURE__ */ React.createElement("div", {
    id: "daily-incidents",
    style: { width: "100%", height: 300, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(ResponsiveContainer, null, /* @__PURE__ */ React.createElement(ScatterChart, {
    data: dataPoints
  }, /* @__PURE__ */ React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /* @__PURE__ */ React.createElement(XAxis, {
    dataKey: "day",
    name: "Day"
  }), /* @__PURE__ */ React.createElement(YAxis, {
    dataKey: "total",
    name: "Total"
  }), /* @__PURE__ */ React.createElement(Tooltip$1, {
    content: /* @__PURE__ */ React.createElement(FilterZeroTooltip, null)
  }), /* @__PURE__ */ React.createElement(Scatter, {
    name: "day",
    data: dataPoints,
    fill: "#8884d8"
  }))));
};
const DailyIncidents = ({ context }) => {
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Incidents by day",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: "daily-incidents"
    })
  }, /* @__PURE__ */ React.createElement(Graph, {
    context
  }));
};

const useErrorOutlineStyles = makeStyles((theme) => ({
  root: {
    marginRight: theme.spacing(1),
    fill: theme.palette.infoText
  }
}));
const ErrorOutlineStyled = () => {
  const classes = useErrorOutlineStyles();
  return /* @__PURE__ */ React.createElement(ErrorOutline, {
    classes
  });
};
const ExpandMoreIconStyled = () => {
  const classes = useErrorOutlineStyles();
  return /* @__PURE__ */ React.createElement(ExpandMoreIcon, {
    classes
  });
};
const useStyles$2 = makeStyles((theme) => ({
  panel: {
    backgroundColor: theme.palette.infoBackground,
    color: theme.palette.infoText,
    verticalAlign: "middle",
    marginBottom: "1rem"
  },
  summary: {
    display: "flex",
    flexDirection: "row"
  },
  summaryText: {
    color: theme.palette.infoText,
    fontWeight: "bold"
  },
  message: {
    width: "100%",
    display: "block",
    color: theme.palette.infoText,
    backgroundColor: theme.palette.infoBackground,
    "& > a": {
      textDecoration: "underline"
    }
  },
  details: {
    width: "100%",
    display: "block",
    color: theme.palette.textContrast,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.border}`,
    padding: theme.spacing(2),
    fontFamily: "sans-serif"
  }
}));
const InfoPanel = (props) => {
  const classes = useStyles$2(props);
  const { title, message, children } = props;
  return /* @__PURE__ */ React.createElement(Accordion, {
    className: classes.panel
  }, /* @__PURE__ */ React.createElement(AccordionSummary, {
    expandIcon: /* @__PURE__ */ React.createElement(ExpandMoreIconStyled, null),
    className: classes.summary
  }, /* @__PURE__ */ React.createElement(ErrorOutlineStyled, null), /* @__PURE__ */ React.createElement(Typography, {
    className: classes.summaryText,
    variant: "subtitle1"
  }, title)), (message || children) && /* @__PURE__ */ React.createElement(AccordionDetails, null, /* @__PURE__ */ React.createElement(Grid, {
    container: true
  }, message && /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    xs: 12
  }, /* @__PURE__ */ React.createElement(Typography, {
    className: classes.message,
    variant: "body1"
  }, message)), children && /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    xs: 12,
    className: classes.details
  }, children))));
};

const WeeklyImpactResponders = ({ context }) => {
  const graphId = "weekly-impact-responders";
  const analyticsApi = useApi(analyticsApiRef);
  const data = analyticsApi.impactByWeekAndResponder(context);
  return /* @__PURE__ */ React.createElement(InfoCard, {
    title: "Average impact by week and responder",
    action: /* @__PURE__ */ React.createElement(SaveAction, {
      targetRef: graphId
    })
  }, /* @__PURE__ */ React.createElement("div", {
    id: graphId,
    style: { width: "100%", height: 450, paddingTop: "1.2rem", paddingRight: "1.2rem" }
  }, /* @__PURE__ */ React.createElement(ResponsiveContainer, null, /* @__PURE__ */ React.createElement(ComposedChart, {
    data: data.dataPoints
  }, /* @__PURE__ */ React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /* @__PURE__ */ React.createElement(XAxis, {
    dataKey: "period"
  }), /* @__PURE__ */ React.createElement(YAxis, {
    tickFormatter: (value) => value === 0 ? "0 min" : moment.duration(value, "minutes").humanize()
  }), data.responders.map((responder) => /* @__PURE__ */ React.createElement(Bar, {
    dataKey: responder,
    fill: colorForString(responder),
    stackId: "a",
    barSize: 30,
    key: responder
  })), /* @__PURE__ */ React.createElement(Tooltip$1, {
    formatter: (value, name) => [value === 0 ? "0 min" : moment.duration(value, "minutes").humanize(), name],
    content: /* @__PURE__ */ React.createElement(FilterZeroTooltip, null)
  }), /* @__PURE__ */ React.createElement(Legend, null)))));
};

const Analytics = () => {
  const configApi = useApi(configApiRef);
  const opsgenieApi = useApi(opsgenieApiRef);
  const from = moment().subtract(1, "year").startOf("quarter");
  const to = moment();
  const { value: data, loading, error } = useAsync(async () => {
    return Promise.all([
      opsgenieApi.getIncidents({
        limit: 100,
        query: `createdAt < ${to.valueOf()} AND createdAt > ${from.valueOf()}`
      }),
      opsgenieApi.getTeams()
    ]);
  });
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert$1, {
      severity: "error"
    }, error.message);
  }
  const context = {
    from,
    to,
    incidents: data[0].filter((incident) => moment(incident.impactStartDate).isAfter(from)),
    teams: data[1]
  };
  const businessHours = {
    start: configApi.getOptionalNumber("opsgenie.analytics.businessHours.start") || DEFAULT_BUSINESS_HOURS_START,
    end: configApi.getOptionalNumber("opsgenie.analytics.businessHours.end") || DEFAULT_BUSINESS_HOURS_END
  };
  return /* @__PURE__ */ React.createElement(Grid, {
    container: true,
    spacing: 3
  }, /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    xs: 12
  }, /* @__PURE__ */ React.createElement(InfoPanel, {
    title: "This graphs cover one year worth of incidents, from the current quarter to the same quarter last year.",
    message: /* @__PURE__ */ React.createElement("ul", null, /* @__PURE__ */ React.createElement("li", null, "Incidents from ", from.format("LL"), " to now are used"), /* @__PURE__ */ React.createElement("li", null, "Business hours are ", businessHours.start, " to ", businessHours.end), /* @__PURE__ */ React.createElement("li", null, "Responders are read from the ", /* @__PURE__ */ React.createElement("code", null, "responders"), ' incident extra property if defined, or from the "responders" section of an incident.'))
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(WeeklyIncidents, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(WeeklyIncidentsSeverity, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(WeeklyIncidentsResponders, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(MonthlyIncidentsResponders, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(QuarterlyIncidentsResponders, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(DailyIncidentsResponders, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(HourlyIncidents, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(DailyIncidents, {
    context
  })), /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    md: 6,
    xs: 12
  }, /* @__PURE__ */ React.createElement(WeeklyImpactResponders, {
    context
  })));
};

const Route = () => null;
attachComponentData(Route, "core.gatherMountPoints", true);
function createSubRoutesFromChildren(childrenProps) {
  const routeType = (/* @__PURE__ */ React.createElement(Route, {
    path: "",
    title: ""
  }, /* @__PURE__ */ React.createElement("div", null))).type;
  return Children.toArray(childrenProps).flatMap((child) => {
    if (!isValidElement(child)) {
      return [];
    }
    if (child.type === Fragment) {
      return createSubRoutesFromChildren(child.props.children);
    }
    if (child.type !== routeType) {
      throw new Error("Child of ExploreLayout must be an ExploreLayout.Route");
    }
    const { path, title, children, tabProps } = child.props;
    return [{ path, title, children, tabProps }];
  });
}
const Layout = ({ children }) => {
  const routes = createSubRoutesFromChildren(children);
  return /* @__PURE__ */ React.createElement(Page, {
    themeId: "tool"
  }, /* @__PURE__ */ React.createElement(Header, {
    title: "Opsgenie"
  }), /* @__PURE__ */ React.createElement(RoutedTabs, {
    routes
  }));
};
Layout.Route = Route;

const useStyles$1 = makeStyles({
  denseListIcon: {
    marginRight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  }
});
const AlertStatus = ({ alert }) => {
  const classes = useStyles$1();
  return /* @__PURE__ */ React.createElement(Tooltip, {
    title: alert.status,
    placement: "top"
  }, /* @__PURE__ */ React.createElement("div", {
    className: classes.denseListIcon
  }, alert.status === "open" ? /* @__PURE__ */ React.createElement(StatusError, null) : /* @__PURE__ */ React.createElement(StatusOK, null)));
};

const useStyles = makeStyles({
  listItemPrimary: {
    fontWeight: "bold"
  },
  listItemIcon: {
    minWidth: "1em"
  }
});
const AlertListItem = ({ alert }) => {
  const classes = useStyles();
  const [alertState, setAlertState] = useState({ data: alert, updatedAt: alert.updatedAt });
  const onAlertChanged = (newAlert) => {
    setAlertState({
      data: newAlert,
      updatedAt: new Date().toISOString()
    });
  };
  return /* @__PURE__ */ React.createElement(ListItem, {
    dense: true,
    key: alertState.data.id
  }, /* @__PURE__ */ React.createElement(ListItemIcon, {
    className: classes.listItemIcon
  }, /* @__PURE__ */ React.createElement(AlertStatus, {
    alert: alertState.data
  })), /* @__PURE__ */ React.createElement(ListItemText, {
    primary: alertState.data.message,
    primaryTypographyProps: {
      variant: "body1",
      className: classes.listItemPrimary
    },
    secondary: /* @__PURE__ */ React.createElement(Typography, {
      noWrap: true,
      variant: "body2",
      color: "textSecondary"
    }, "Created ", moment(alertState.data.createdAt).fromNow())
  }), /* @__PURE__ */ React.createElement(ListItemSecondaryAction, null, /* @__PURE__ */ React.createElement(AlertActionsMenu, {
    alert: alertState.data,
    onAlertChanged
  })));
};
const AlertsSummaryTable = ({ alerts }) => {
  return /* @__PURE__ */ React.createElement(List, {
    dense: true
  }, alerts.map((alert, index) => /* @__PURE__ */ React.createElement(AlertListItem, {
    key: alert.id + index,
    alert
  })), alerts.length === 0 && /* @__PURE__ */ React.createElement(React.Fragment, null, "No recent alerts"));
};
const AlertsSummary = ({ query }) => {
  const opsgenieApi = useApi(opsgenieApiRef);
  const { value, loading, error } = useAsync(async () => await opsgenieApi.getAlerts({ limit: 3, query }));
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert$1, {
      "data-testid": "error-message",
      severity: "error"
    }, error.message);
  }
  return /* @__PURE__ */ React.createElement(AlertsSummaryTable, {
    alerts: value
  });
};

export { AlertsList as A, EntityOpsgenieAlertsCard as E, IncidentsList as I, Layout as L, OnCallList as O, Analytics as a, OPSGENIE_ANNOTATION as b, AlertsSummary as c, OPSGENIE_TEAM_ANNOTATION as d, OnCallForScheduleList as e, opsGeniePlugin as f, OpsgeniePage as g, EntityOpsgenieOnCallListCard as h, OpsgenieApi as i, isOpsgenieAvailable as j, isOpsgenieOnCallListAvailable as k, opsgenieApiRef as o };
//# sourceMappingURL=index-d2863483.esm.js.map
